import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import "./ImageModal.css";

const ImageModal = ({ isOpen, image, title, onClose }) => {
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden"; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !image) {
    return null;
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div className="image-modal-overlay" onClick={handleBackdropClick}>
      <div className="image-modal">
        <div className="image-modal-header">
          <h3 className="image-modal-title">{title}</h3>
          <button
            className="image-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="image-modal-content">
          <img
            src={image}
            alt={title}
            className="image-modal-image"
            onError={(e) => {
              e.target.src =
                "https://via.placeholder.com/800x600?text=Image+Not+Available";
            }}
          />
        </div>

        <div className="image-modal-footer">
          <button
            className="image-modal-download"
            onClick={() => {
              const link = document.createElement("a");
              link.href = image;
              link.download = `${title.replace(/\s+/g, "_")}.jpg`;
              link.click();
            }}
            aria-label="Download image"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7,10 12,15 17,10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download
          </button>

          <div className="image-modal-actions">
            <button
              className="image-modal-action-btn"
              onClick={() => {
                navigator.clipboard.writeText(image);
                // You could add a toast notification here
              }}
              aria-label="Copy image URL"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Copy URL
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use Portal to render at root level
  return createPortal(modalContent, document.body);
};

export default ImageModal;
